/**
 * Anonymous Status Debug Component
 * Shows current anonymous authentication status for debugging
 * Only visible in development mode
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Chip, Typography } from '@mui/material';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';

const AnonymousStatusDebug = () => {
  // Hooks must be called at the very top before any conditional returns
  const {
    isAnonymous,
    isLoggedIn,
    userData,
    syncCartFromFirebase,
    getDebugAuthState,
  } = useAnonymousAuth();
  const cart = useSelector((state) => state.shopReducer.cart);

  // Get detailed debug state
  const debugState = getDebugAuthState ? getDebugAuthState() : null;

  // Add a manual sync button for debugging
  const handleSyncCart = React.useCallback(async () => {
    if (syncCartFromFirebase) {
      console.log('🔄 [DEBUG] Manual cart sync triggered');
      const result = await syncCartFromFirebase();
      console.log('🔄 [DEBUG] Sync result:', result);
    }
  }, [syncCartFromFirebase]);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  // If hook fails to initialize properly, show warning
  if (typeof isAnonymous === 'undefined' || typeof isLoggedIn === 'undefined') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 10,
          right: 10,
          zIndex: 9999,
          backgroundColor: 'rgba(255, 165, 0, 0.8)',
          color: 'white',
          padding: 2,
          borderRadius: 1,
          fontSize: '0.75rem',
          maxWidth: 300,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          <span role="img" aria-label="warning">
            ⚠️
          </span>{' '}
          Auth Debug Warning
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Auth service initializing...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 10,
        left: 10,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: 2,
        borderRadius: 1,
        fontSize: '0.75rem',
        maxWidth: 300,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
      >
        <span role="img" aria-label="lock">
          🔐
        </span>{' '}
        Auth Debug Status
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Chip
          label={isLoggedIn ? 'Logged In' : 'Not Logged In'}
          color={isLoggedIn ? 'success' : 'error'}
          size="small"
        />

        {isLoggedIn && (
          <Chip
            label={isAnonymous ? 'Anonymous' : 'Authenticated'}
            color={isAnonymous ? 'warning' : 'success'}
            size="small"
          />
        )}
      </Box>

      <Typography variant="caption" sx={{ display: 'block' }}>
        Cart Items: {cart?.length || 0}
      </Typography>

      <Typography variant="caption" sx={{ display: 'block' }}>
        User ID: {userData?.uid || 'None'}
      </Typography>

      {debugState && (
        <>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.65rem', mt: 1 }}
          >
            <strong>Debug Info:</strong>
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.6rem' }}
          >
            UIDs Match: {debugState.consistency.uidsMatch ? '✅' : '❌'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.6rem' }}
          >
            Anonymous Match:{' '}
            {debugState.consistency.anonymousStatusMatch ? '✅' : '❌'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.6rem' }}
          >
            UserData UID: {debugState.consistency.userDataHasUid ? '✅' : '❌'}
          </Typography>
        </>
      )}

      {isAnonymous && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: '#ffa726' }}
        >
          <span role="img" aria-label="warning">
            ⚠️
          </span>{' '}
          Will need auth at checkout
        </Typography>
      )}

      {syncCartFromFirebase && (
        <button
          onClick={handleSyncCart}
          style={{
            marginTop: '8px',
            padding: '4px 8px',
            fontSize: '0.7rem',
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          <span role="img" aria-label="sync">
            🔄
          </span>{' '}
          Sync Cart
        </button>
      )}
    </Box>
  );
};

export default AnonymousStatusDebug;
